import { Navigate, NavLink, Route, Routes } from "react-router-dom";
import type { AccountRole } from "../../../shared/api-types";
import { useT } from "../../i18n";
import { usePageCrumbs } from "../../lib/page-header";
import { CompanySettings } from "./CompanySettings";
import { MembersSettings } from "./MembersSettings";
import { ProfileSettings } from "./ProfileSettings";

function navClass({ isActive }: { isActive: boolean }): string {
  return `block rounded-lg px-3 py-2 text-sm transition-colors md:px-2.5 md:py-1.5 ${
    isActive
      ? "bg-zinc-100 font-medium text-zinc-900 dark:bg-zinc-800 dark:text-zinc-50"
      : "text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100"
  }`;
}

/**
 * Einstellungen: Profil (eigenes Konto) und darunter der AKTIVE Account —
 * Allgemein (Name, Speicher, später Billing) und Benutzer (nur Account-Admins).
 * Andere Accounts erreicht man über den Umschalter im Header.
 */
export function SettingsPage({
  accountRole,
  accountName,
  myUserId,
}: {
  /** Rolle im aktiven Account; null ohne Mitgliedschaft */
  accountRole: AccountRole | null;
  /** Name des aktiven Accounts — beschriftet die Kontogruppe in der Subnav */
  accountName: string | null;
  myUserId: string;
}) {
  const t = useT();
  const isAdmin = accountRole === "owner";

  usePageCrumbs([{ label: t.settings.title }]);

  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-8">
      <h2 className="mb-6 text-lg font-semibold">{t.settings.title}</h2>
      <div className="flex flex-col gap-6 md:flex-row md:gap-10">
        {/* Absolute Ziele: relative Links würden in der Splat-Route (/settings/*)
            gegen den schon gematchten Pfad aufgelöst — /profile/profile/… */}
        <nav className="flex shrink-0 flex-wrap gap-1 md:w-44 md:flex-col">
          <NavLink to="/settings/profile" className={navClass}>
            {t.settings.navProfile}
          </NavLink>
          {accountRole && (
            <>
              {/* w-full erzwingt in der umbrechenden Mobil-Reihe eine eigene Zeile:
                  ohne diese Überschrift stünde dort ein kontextloses „Allgemein“. */}
              <p
                className="w-full truncate px-3 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-400 md:px-2.5 md:pt-5"
                title={accountName ?? undefined}
              >
                {accountName ?? t.settings.navAccountGroup}
              </p>
              <NavLink to="/settings/account" className={navClass}>
                {t.settings.navGeneral}
              </NavLink>
              {isAdmin && (
                <NavLink to="/settings/users" className={navClass}>
                  {t.settings.navUsers}
                </NavLink>
              )}
            </>
          )}
        </nav>
        <div className="min-w-0 flex-1">
          <Routes>
            <Route index element={<Navigate to="/settings/profile" replace />} />
            <Route path="profile" element={<ProfileSettings />} />
            <Route
              path="account"
              element={
                accountRole ? <CompanySettings /> : <Navigate to="/settings/profile" replace />
              }
            />
            <Route
              path="users"
              element={
                isAdmin ? (
                  <MembersSettings myUserId={myUserId} />
                ) : (
                  <Navigate to="/settings/profile" replace />
                )
              }
            />
            <Route path="*" element={<Navigate to="/settings/profile" replace />} />
          </Routes>
        </div>
      </div>
    </main>
  );
}
